import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const OnboardingSportsPage: React.FC = () => {
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const sports = [
    { name: 'Football', emoji: '⚽', id: 'football' },
    { name: 'Cricket', emoji: '🏏', id: 'cricket' },
    { name: 'Tennis', emoji: '🎾', id: 'tennis' },
    { name: 'Chess', emoji: '♟️', id: 'chess' },
    { name: 'Basketball', emoji: '🏀', id: 'basketball' },
    { name: 'Badminton', emoji: '🏸', id: 'badminton' },
    { name: 'Swimming', emoji: '🏊', id: 'swimming' },
    { name: 'Athletics', emoji: '🏃', id: 'athletics' },
  ];

  const sportColors = {
    Football: 'border-blue-500 bg-blue-50 text-blue-700',
    Cricket: 'border-red-500 bg-red-50 text-red-700',
    Tennis: 'border-green-500 bg-green-50 text-green-700',
    Chess: 'border-gray-500 bg-gray-50 text-gray-700',
    Basketball: 'border-orange-500 bg-orange-50 text-orange-700',
    Badminton: 'border-yellow-500 bg-yellow-50 text-yellow-700',
    Swimming: 'border-cyan-500 bg-cyan-50 text-cyan-700',
    Athletics: 'border-purple-500 bg-purple-50 text-purple-700',
  };

  useEffect(() => {
    // Check if user came from signup
    const signupData = localStorage.getItem('signupData');
    if (!signupData) {
      navigate('/signup');
    }
  }, [navigate]);

  const toggleSport = (sportId: string) => {
    setSelectedSports(prev => 
      prev.includes(sportId) 
        ? prev.filter(id => id !== sportId)
        : [...prev, sportId]
    );
  };

  const handleContinue = async () => {
    if (selectedSports.length === 0) {
      toast.error('Please select at least one sport');
      return;
    }

    setLoading(true);

    try {
      // Store selected sports
      const signupData = JSON.parse(localStorage.getItem('signupData') || '{}');
      signupData.sports = selectedSports;
      localStorage.setItem('signupData', JSON.stringify(signupData));
      
      // Navigate to onboarding slides
      navigate('/onboarding/slides');
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <Link to="/signup" className="text-gray-600 hover:text-gray-800 mb-4 inline-block">
            ← Back
          </Link>
          <div className="flex justify-center space-x-2 mb-6">
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Choose Sports</h1>
          <p className="text-gray-600 mt-2">Select sports you're interested in</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {sports.map((sport) => (
            <button
              key={sport.id}
              onClick={() => toggleSport(sport.id)}
              className={`p-4 border-2 rounded-lg transition-all text-center ${
                selectedSports.includes(sport.id)
                  ? sportColors[sport.id as keyof typeof sportColors]
                  : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
              }`}
            >
              <div className="text-3xl mb-2">{sport.emoji}</div>
              <div className="font-medium">{sport.name}</div>
            </button>
          ))}
        </div>

        {selectedSports.length > 0 && (
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">Selected Sports:</p>
            <div className="flex flex-wrap gap-2">
              {selectedSports.map(sportId => {
                const sport = sports.find(s => s.id === sportId);
                return (
                  <span 
                    key={sportId}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      sportColors[sportId as keyof typeof sportColors]
                    }`}
                  >
                    <span className="mr-1">{sport?.emoji}</span>
                    {sport?.name}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <button
          onClick={handleContinue}
          disabled={loading || selectedSports.length === 0}
          className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Processing...' : 'Continue to Onboarding →'}
        </button>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-yellow-600 hover:text-yellow-800 font-medium">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSportsPage;